import { ENotificationCategory } from 'multirpc-sdk';
import { useMemo } from 'react';

import { useAccount } from 'modules/account/hooks/useAccount';

import { useNotifications } from './useNotifications';

export interface IUnseenNotificationsCounts {
  billing: number;
  news: number;
  system: number;
  /**
   * Sum of the three known categories. Used where the filter row only offers
   * those categories, so the "all" tag has to agree with the sum of its
   * siblings.
   */
  categorized: number;
  /**
   * Every unseen notification, including ones in a category the filter row
   * does not render.
   */
  total: number;
}

/**
 * Shared unseen-notification tallies for the notification filter rows.
 * Both the notifications page and the layout notifications menu need the same
 * query and the same per-category counts.
 */
export const useUnseenNotificationsCounts = (): IUnseenNotificationsCounts => {
  const { group } = useAccount();

  const { notifications: notificationsResponse } = useNotifications({
    skipFetching: true,
    only_unseen: true,
    group,
  });

  const unseen = notificationsResponse?.notifications;

  return useMemo(() => {
    const notifications = unseen ?? [];

    const countOf = (category: ENotificationCategory) =>
      notifications.filter(notification => notification.category === category)
        .length;

    const billing = countOf(ENotificationCategory.BILLING);
    const news = countOf(ENotificationCategory.NEWS);
    const system = countOf(ENotificationCategory.SYSTEM);

    return {
      billing,
      categorized: billing + news + system,
      news,
      system,
      total: notifications.length,
    };
  }, [unseen]);
};
